export const GUIDANCE_LANGUAGES = ['en', 'vi'] as const;
export type GuidanceLanguage = (typeof GUIDANCE_LANGUAGES)[number];

export const GUIDANCE_LANGUAGE_OPTIONS: ReadonlyArray<{ value: GuidanceLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'vi', label: 'Tiếng Việt' },
];

const STORAGE_KEY = 'hayven.agency.guidance.language';

export function isGuidanceLanguage(value: unknown): value is GuidanceLanguage {
  return GUIDANCE_LANGUAGES.some((language) => language === value);
}

/** `?lang=` when a shared link names one; the key is left out otherwise, so a plain link follows the reader's choice. */
export function parseGuidanceSearch(search: Record<string, unknown>): { lang?: GuidanceLanguage } {
  return isGuidanceLanguage(search.lang) ? { lang: search.lang } : {};
}

/** The reader's last pick, else their browser's language: the team reads in Vietnamese or in English. */
export function preferredGuidanceLanguage(): GuidanceLanguage {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isGuidanceLanguage(saved)) return saved;
  } catch {
    // Storage can be blocked; the browser's language still answers.
  }
  return navigator.language.toLowerCase().startsWith('vi') ? 'vi' : 'en';
}

export function rememberGuidanceLanguage(language: GuidanceLanguage): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Only a convenience: the pick still applies through the URL.
  }
}

export const GUIDANCE_COPY = {
  en: {
    title: 'Guidance',
    description: 'Team docs. Only agency members can open these.',
    back: 'All guidance',
    readMinutes: (minutes: number) => `${minutes} min read`,
    updated: (date: string) => `Updated ${date}`,
    loadListError: 'Couldn’t Load Guidance',
    loadDocError: 'Couldn’t Open This Doc',
    docGone: 'This doc isn’t available',
    docGoneHint: 'The link may be old. Go back to All guidance.',
    retryHint: 'Check your connection and try again.',
    empty: 'No docs yet.',
  },
  vi: {
    title: 'Tài liệu team',
    description: 'Tài liệu nội bộ. Chỉ thành viên agency mới mở được.',
    back: 'Tất cả tài liệu',
    readMinutes: (minutes: number) => `Đọc ${minutes} phút`,
    updated: (date: string) => `Cập nhật ${date}`,
    loadListError: 'Không tải được tài liệu',
    loadDocError: 'Không mở được tài liệu này',
    docGone: 'Tài liệu này không còn',
    docGoneHint: 'Có thể link đã cũ. Quay lại Tất cả tài liệu.',
    retryHint: 'Kiểm tra kết nối rồi thử lại.',
    empty: 'Chưa có tài liệu.',
  },
} as const;

/** `updatedOn` is a plain "YYYY-MM-DD" date, so it is read in UTC and can't shift by the reader's time zone. */
export function formatGuidanceDate(date: string, language: GuidanceLanguage): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US', {
    day: 'numeric',
    month: language === 'vi' ? 'numeric' : 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
